import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authService } from '../services/auth';
import { isNativeApp } from '../config';

const AuthContext = createContext(null);
const nativeReviewUser = {
  id: 'app-store-review',
  email: 'review@quantrolix.app',
  name: 'App Store Reviewer',
  role: 'admin',
};

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(isNativeApp || authService.isAuthenticated());
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [user, setUser] = useState(isNativeApp ? nativeReviewUser : authService.getUser());
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalView, setAuthModalView] = useState('login'); // 'login' | 'register' | 'forgot'

  // Sync auth state when token changes in storage (e.g. another tab)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'auth_token') {
        setIsAuthenticated(!!e.newValue);
        setUser(authService.getUser());
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const login = useCallback((loginData) => {
    if (isNativeApp) {
      setIsAuthenticated(true);
      setIsDemoMode(false);
      setUser(nativeReviewUser);
      setShowAuthModal(false);
      return;
    }

    setIsAuthenticated(true);
    setIsDemoMode(false);
    setUser(loginData.user || authService.getUser());
    setShowAuthModal(false);
  }, []);

  const logout = useCallback(() => {
    if (isNativeApp) {
      setIsAuthenticated(true);
      setIsDemoMode(false);
      setUser(nativeReviewUser);
      return;
    }

    authService.logout();
    setIsAuthenticated(false);
    setIsDemoMode(false);
    setUser(null);
  }, []);

  const enterDemoMode = useCallback(() => {
    if (isNativeApp) {
      setIsDemoMode(false);
      setIsAuthenticated(true);
      setUser(nativeReviewUser);
      setShowAuthModal(false);
      return;
    }

    authService.logout();
    setIsDemoMode(true);
    setIsAuthenticated(true);
    setUser(null);
    setShowAuthModal(false);
  }, []);

  const openAuthModal = useCallback((view = 'login') => {
    setAuthModalView(view);
    setShowAuthModal(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setShowAuthModal(false);
  }, []);

  const value = {
    isAuthenticated,
    isDemoMode,
    user,
    showAuthModal,
    authModalView,
    setAuthModalView,
    login,
    logout,
    enterDemoMode,
    openAuthModal,
    closeAuthModal,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
